import { useMemo } from 'react';
import { useI18nContext } from '@/i18n/i18n-react';
import type {
  CampaignDto,
  TenantCampaignOrdersChartResponseDto,
} from '@/generated/korza-api-client/types.gen';
import { bucketStartIso, type ChartGranularity } from '@/lib/chart-axis';
import { groupCampaignsByBucket, markerTime } from '@/lib/campaign-markers';
import { bucketSumChartData } from '@/lib/hooks/use-chart-data';
import { useChartTooltip } from '@/lib/hooks/use-chart-tooltip';
import { usePartialBuckets } from '@/lib/hooks/use-partial-buckets';
import { markPartialRows, type ChartRange } from '@/lib/partial-buckets';
import { MetricsAreaChart } from '@/components/ui/charts/area-chart';
import { MetricsChartAreaSeries } from '@/components/ui/charts/chart-area-series';
import { ChartCampaignMarkers } from '@/components/ui/charts/chart-campaign-markers';
import { ChartClickHint } from '@/components/ui/charts/chart-click-hint';

type CampaignOrdersPoint =
  TenantCampaignOrdersChartResponseDto['points'][number];

interface CampaignOrdersRevenueChartProps {
  granularity: ChartGranularity;
  points: CampaignOrdersPoint[];
  currency: string;
  /** The filter's period: marks the partial first/last bucket (CHART-01). */
  range?: ChartRange;
  /** Campaigns sent in the period: a dot on each bucket with campaigns (HOME-04). */
  campaigns?: CampaignDto[];
  syncId?: string;
  syncMethod?: 'index' | 'value';
  /**
   * When provided, each period is clickable; `bucketStart` is the point's
   * original api date.
   */
  onBucketClick?: (bucketStart: string) => void;
}

export function CampaignOrdersRevenueChart({
  granularity,
  points,
  currency,
  range,
  campaigns,
  syncId,
  syncMethod,
  onBucketClick,
}: CampaignOrdersRevenueChartProps) {
  const { LL, locale } = useI18nContext();

  const lineChartData = useMemo(
    () =>
      bucketSumChartData(
        points,
        granularity,
        locale,
        (p) => p.revenue,
        'revenue'
      ),
    [granularity, locale, points]
  );
  const rows = useMemo(
    () =>
      lineChartData.map((row) => ({
        month: row.month,
        date: String(row.date),
        revenue: Number(row.revenue),
      })),
    [lineChartData]
  );
  const partial = usePartialBuckets(rows, range, granularity);
  const chartData = useMemo(
    () => markPartialRows(rows, 'revenue', partial.edges),
    [rows, partial.edges]
  );
  const groups = useMemo(
    () =>
      range && campaigns
        ? groupCampaignsByBucket(campaigns, granularity, locale, range)
        : new Map<string, CampaignDto[]>(),
    [campaigns, granularity, locale, range]
  );
  const campaignsOfRow = (row: Record<string, unknown>) => {
    const key =
      typeof row.date === 'string'
        ? bucketStartIso(row.date, granularity, locale)
        : null;
    return (key ? (groups.get(key) ?? []) : []).map((campaign) => ({
      name: campaign.name,
      time: markerTime(campaign.launchDateTime, granularity, locale),
    }));
  };

  const handleBucketClick = onBucketClick
    ? (row: Record<string, unknown>) => {
        if (typeof row.date === 'string') onBucketClick(row.date);
      }
    : undefined;

  const tooltipContent = useChartTooltip({
    footer: onBucketClick ? <ChartClickHint /> : undefined,
    campaigns: campaignsOfRow,
    formatRows: (_label, payload) => [
      {
        label: LL.metricsPage.charts.campaignOrdersRevenue.title(),
        value: new Intl.NumberFormat(locale, {
          style: 'currency',
          currency,
        }).format((payload[0]?.value as number) ?? 0),
      },
    ],
  });

  return (
    <MetricsAreaChart
      data={chartData}
      partialLegend={partial.edges}
      syncId={syncId}
      syncMethod={syncMethod}
      tooltipContent={tooltipContent}
      onBucketClick={handleBucketClick}
    >
      <MetricsChartAreaSeries
        dataKey="revenue"
        partialDataKey="revenuePartial"
        partialLabels={partial.labels}
      />
      <ChartCampaignMarkers
        data={chartData}
        groups={groups}
        granularity={granularity}
        valueKey="revenue"
      />
    </MetricsAreaChart>
  );
}
